<template>
  <!-- Record header for an open job tab: mirrors the values shown in a list-view
       row so they stay copyable inside the tab. The list's "Details" button
       becomes a "Close tab" button here. -->
  <div class="gid-job-summary">
    <div class="gid-job-summary__bar">
      <div class="gid-job-summary__title">
        <a
          :href="`../jobs/${item.job.name}`"
          class="gid-job-summary__jobno"
          target="_blank"
        >
          {{ item.job.name }}
        </a>
        <b-icon-clipboard
          class="gid-job-summary__copy"
          role="button"
          :title="$t('job.table.heading.job')"
          @click="copy(item.job.name)"
        />
        <job-types :job="item" />
      </div>
      <div class="gid-job-summary__actions">
        <job-docs-dropdown
          :files="item.files"
          :job-name="item.job.name"
          :job-sfid="item.sfid"
          :jmf-picklist="jmfPicklist"
        />
        <b-button
          size="sm"
          variant="light"
          class="gid-job-summary__refresh"
          :disabled="refreshing"
          :title="$t('job.console.refresh')"
          @click="$emit('refresh')"
        >
          <span
            class="material-icons"
            :class="{ 'gid-job-summary__spin': refreshing }"
          >
            refresh
          </span>
        </b-button>
        <b-button
          size="sm"
          variant="light"
          class="gid-job-summary__close"
          @click="$emit('close')"
        >
          <span class="material-icons">close</span>
          {{ $t('job.console.close') }}
        </b-button>
      </div>
    </div>

    <div class="gid-job-summary__grid">
      <!-- Job (EIDs / ids) -->
      <div class="gid-job-summary__field">
        <span class="gid-job-summary__label">
          {{ $t('job.table.heading.job') }}
        </span>
        <div class="gid-job-summary__value">
          <div
            v-if="item.job.eid"
            role="button"
            class="gid-job-summary__sub"
            @click="copy(item.job.eid)"
          >
            EID: {{ item.job.eid }}
          </div>
          <div
            v-if="item.job.invoicing_eid"
            role="button"
            class="gid-job-summary__sub"
            @click="copy(item.job.invoicing_eid)"
          >
            Inv. EID: {{ item.job.invoicing_eid }}
          </div>
          <div
            v-if="item.job.partner_eid"
            role="button"
            class="gid-job-summary__sub"
            @click="copy(item.job.partner_eid)"
          >
            Partner EID: {{ item.job.partner_eid }}
          </div>
          <div
            v-if="item.job.s4c_order_id"
            role="button"
            class="gid-job-summary__sub"
            :title="$t('job.s4c-open-hint')"
            @click="onS4cClick"
            @dblclick="onS4cDblClick"
          >
            S4C: {{ item.job.s4c_order_id }}
          </div>
          <div
            v-if="item.job.affiliate_id"
            role="button"
            class="gid-job-summary__sub"
            @click="copy(item.job.affiliate_id)"
          >
            AID: {{ item.job.affiliate_id }}
          </div>
          <span
            v-if="
              !item.job.eid &&
              !item.job.invoicing_eid &&
              !item.job.partner_eid &&
              !item.job.s4c_order_id &&
              !item.job.affiliate_id
            "
            class="gid-job-summary__muted"
          >
            —
          </span>
        </div>
      </div>

      <!-- Customer -->
      <div class="gid-job-summary__field">
        <span class="gid-job-summary__label">
          {{ $t('job.table.heading.customer') }}
        </span>
        <div class="gid-job-summary__value">
          <div class="gid-job-summary__line">
            <span
              role="button"
              class="clickable-text"
              @click="$emit('customer-click', item)"
            >
              {{ customerFullName }}
            </span>
            <b-icon-clipboard
              v-if="customerFullName"
              class="gid-job-summary__copy"
              role="button"
              @click="copy(customerFullName)"
            />
          </div>
          <template
            v-if="
              item.job_contact_person &&
              item.job_contact_person.first_name &&
              item.job_contact_person.last_name
            "
          >
            <div class="gid-job-summary__sub">
              <strong>{{ $t('inputs.contact_person.title') }}:</strong>
              {{ item.job_contact_person.first_name }}
              {{ item.job_contact_person.last_name }}
            </div>
            <call-contact
              :job-api-id="item.job.api_id"
              :contact-list="[
                {
                  phone: item.job_contact_person.phone,
                  jobRole: customerRole[0],
                  firstName: item.job_contact_person.first_name,
                  lastName: item.job_contact_person.last_name,
                },
              ]"
              :role-filter="customerRole"
              @call-initiated="$emit('call-initiated', $event)"
            />
          </template>
          <call-contact
            v-else-if="jobContacts && jobContacts.length"
            :job-api-id="item.job.api_id"
            :contact-list="jobContacts"
            :role-filter="customerRole"
            @call-initiated="$emit('call-initiated', $event)"
          />
        </div>
      </div>

      <!-- Opportunity / project -->
      <div class="gid-job-summary__field">
        <span class="gid-job-summary__label">
          {{ $t('job.table.heading.opportunity') }}
        </span>
        <div class="gid-job-summary__value">
          <div class="gid-job-summary__line">
            <span
              :id="opportunityHintTarget"
              role="button"
              class="clickable-text"
              @click="$emit('opportunity-click', item)"
            >
              {{ item.opportunity && item.opportunity.name }}
            </span>
            <b-tooltip
              v-if="opportunityHint"
              :target="opportunityHintTarget"
              triggers="hover"
            >
              {{ opportunityHint }}
            </b-tooltip>
            <b-icon-clipboard
              v-if="item.opportunity && item.opportunity.name"
              class="gid-job-summary__copy"
              role="button"
              @click="copy(item.opportunity.name)"
            />
          </div>
          <div v-if="item.project" class="gid-job-summary__sub">
            <span
              role="button"
              class="clickable-text"
              @click="$emit('project-click', item)"
            >
              {{ item.project.name }}
            </span>
          </div>
          <call-contact
            v-if="jobContacts && jobContacts.length"
            :job-api-id="item.job.api_id"
            :contact-list="jobContacts"
            :role-filter="brandRole"
            @call-initiated="$emit('call-initiated', $event)"
          />
        </div>
      </div>

      <!-- Partner -->
      <div class="gid-job-summary__field">
        <span class="gid-job-summary__label">
          {{ $t('job.table.heading.partner') }}
        </span>
        <div class="gid-job-summary__value">
          <template v-if="item.job.partner">
            <div class="gid-job-summary__line">
              <span
                role="button"
                class="clickable-text"
                @click="$emit('partner-click', item)"
              >
                {{ item.partner.name }}
              </span>
              <b-icon-clipboard
                v-if="item.partner.name"
                class="gid-job-summary__copy"
                role="button"
                @click="copy(item.partner.name)"
              />
            </div>
            <b-badge
              v-if="item.partner.is_partner_paused"
              variant="warning"
              class="mt-1"
            >
              {{ $t('partner-pause-generic.paused') }}
            </b-badge>
            <div v-if="item.partner.name" class="mt-1">
              <b-badge :variant="partnerExperience.variant">
                {{ partnerExperience.text }}
              </b-badge>
            </div>
            <call-contact
              v-if="jobContacts && jobContacts.length"
              :job-api-id="item.job.api_id"
              :contact-list="jobContacts"
              :role-filter="partnerRole"
              @call-initiated="$emit('call-initiated', $event)"
            />
          </template>
          <span v-else class="gid-job-summary__muted">
            {{ item.num_partners_offer_is_visible_to }}
            ({{ item.num_partners_potential }})
          </span>
        </div>
      </div>

      <!-- ZIP -->
      <div class="gid-job-summary__field">
        <span class="gid-job-summary__label">
          {{ $t('job.table.heading.zip') }}
        </span>
        <div class="gid-job-summary__value">
          <span v-if="postalCode" role="button" @click="copy(postalCode)">
            {{ postalCode }}
          </span>
          <span v-else class="gid-job-summary__muted">—</span>
        </div>
      </div>

      <!-- Price -->
      <div class="gid-job-summary__field">
        <span class="gid-job-summary__label">
          {{ $t('job.table.heading.price') }}
        </span>
        <div class="gid-job-summary__value">
          <job-price :job_view="item" />
        </div>
      </div>

      <!-- Quote / Angebot — mirrors the "Angebot ansehen" links from the list row -->
      <div class="gid-job-summary__field">
        <span class="gid-job-summary__label">
          {{ $t('job.table.heading.quote') }}
        </span>
        <div class="gid-job-summary__value">
          <div class="gid-job-summary__line">
            <a
              href="#"
              class="clickable-text"
              @click.prevent="$emit('view-quote', item.sfid)"
            >
              {{ $t('view-quote') }}
            </a>
          </div>
          <div v-if="showBrandQuoteLink" class="gid-job-summary__sub">
            <a
              href="#"
              class="clickable-text"
              @click.prevent="$emit('view-brand-quote', item.sfid)"
            >
              {{ $t('view-brand-quote') }}
            </a>
          </div>
        </div>
      </div>

      <!-- Created -->
      <div class="gid-job-summary__field">
        <span class="gid-job-summary__label">
          {{ $t('job.table.heading.created_date') }}
        </span>
        <div class="gid-job-summary__value">
          <span
            v-if="item.job.created_date"
            role="button"
            @click="copy($moment(item.job.created_date).format('L'))"
          >
            {{ $moment(item.job.created_date).format('L') }}
          </span>
          <span v-else class="gid-job-summary__muted">—</span>
        </div>
      </div>

      <!-- Appointment / status -->
      <div class="gid-job-summary__field">
        <span class="gid-job-summary__label">
          {{ $t('job.table.heading.appointment') }}
        </span>
        <div class="gid-job-summary__value">
          <div>{{ appointmentRange }}</div>
          <div class="gid-job-summary__sub">{{ readinessLabel }}</div>
          <strong>{{ statusChangeLabel }} {{ statusName }}</strong>
        </div>
      </div>

      <!-- Ampel -->
      <div class="gid-job-summary__field">
        <span class="gid-job-summary__label">
          {{ $t('job.table.heading.ampel') }}
        </span>
        <div class="gid-job-summary__value">
          <div class="d-flex">
            <days-since-last-change
              v-if="showDaysSinceLastStatusChange"
              :days="item.days_since_last_status_change"
            />
            <days-since-last-change
              v-if="item.actions_taken"
              class="ml-3"
              context="action"
              :days="item.actions_taken"
              :action_date="item.latest_action"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BIconClipboard } from 'bootstrap-vue';
import DaysSinceLastChange from '@gid/vue-common/components/DaysSinceLastChange';
import JobTypes from '@gid/vue-common/components/JobTypes.vue';
import {
  GeneralInvoicingTargetEnum,
  JobStatusEnum,
  UserRoleEnum,
} from '@gid/models';
import { _partnerExperienceLevel } from '@gid/vue-common/utils';
import CallContact from '@/components/CallContact.vue';
import { getOpportunityHint } from '@/utils/opportunity';
import { openS4cSalesOrder } from '@/utils/dynamics';
import JobPrice from './JobPrice';
import JobDocsDropdown from './JobDocsDropdown.vue';

export default {
  name: 'JobSummaryHeader',
  components: {
    BIconClipboard,
    DaysSinceLastChange,
    JobTypes,
    CallContact,
    JobPrice,
    JobDocsDropdown,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    jobContacts: {
      type: Array,
      default: () => [],
    },
    jmfPicklist: {
      type: Array,
      default: () => [],
    },
    locale: {
      type: String,
      default: 'de',
    },
    refreshing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Pending single-click copy on the S4C chip; onS4cDblClick cancels it so
      // opening Dynamics does not also fire two "copied" toasts.
      s4cClickTimer: null,
    };
  },
  computed: {
    opportunityHint() {
      return getOpportunityHint(this.item.opportunity, this.locale);
    },
    // Not the job's sfid: the jobs list already renders a tooltip anchored on
    // that id, and a duplicate would bind this tooltip to the list row.
    opportunityHintTarget() {
      return `opportunity-hint-${this.item.job.api_id}`;
    },
    partnerRole() {
      return [GeneralInvoicingTargetEnum.PARTNER];
    },
    brandRole() {
      return [GeneralInvoicingTargetEnum.BRAND];
    },
    customerRole() {
      return [GeneralInvoicingTargetEnum.CUSTOMER];
    },
    // Mirrors Jobs.vue's showBrandQuoteLinks: only offer the brand quote link
    // when the opportunity allows the brand to approve the proposal.
    showBrandQuoteLink() {
      return !!this.item.opportunity?.can_approve_proposal?.includes(
        UserRoleEnum.BRAND,
      );
    },
    customerFullName() {
      const c = this.item.customer;
      if (!c) return '';
      return [
        this.formatCustomerTitle(c.title, c.salutation),
        c.first_name,
        c.last_name,
      ]
        .filter(Boolean)
        .join(' ')
        .trim();
    },
    postalCode() {
      return this.item.customer?.shipping_address?.postal_code || '';
    },
    partnerExperience() {
      return _partnerExperienceLevel(this.item.partner?.completed_jobs);
    },
    showDaysSinceLastStatusChange() {
      return ![JobStatusEnum.COMPLETED].includes(this.item?.job?.status);
    },
    statusName() {
      const desc = this.item.status_description;
      return (
        desc?.gid_name?.[this.locale] ||
        desc?.gid_name?.['en'] ||
        this.item.status
      );
    },
    statusChangeLabel() {
      const date =
        this.item.job.last_status_change ?? this.item.job.created_date;
      return this.$t('job.statuschange', {
        status_change_date: this.$moment(date).format('L'),
      });
    },
    appointmentRange() {
      const start = this.item.job.appointment_start_timestamp;
      const end = this.item.job.appointment_end_timestamp;
      if (!start) return '-';
      const startFmt = this.$moment(start).format('L');
      const endFmt = end ? this.$moment(end).format('L') : '';
      return endFmt && endFmt !== startFmt
        ? `${startFmt} - ${endFmt}`
        : startFmt;
    },
    readinessLabel() {
      const a = this.item.job.readiness_date;
      const b = this.item.job.date_expected;
      const date = a && b ? (a > b ? a : b) : a || b;
      return date
        ? this.$t('job.readyforinstallation', {
            installationdate: this.$moment(date).format('L'),
          })
        : '-';
    },
  },
  methods: {
    formatCustomerTitle(title, salutation) {
      const formatedTitle = (title?.split(' ') || []).map((t) =>
        t.toLowerCase(),
      );
      const formatedSalutation = (salutation?.split(' ') || []).map((t) =>
        t.toLowerCase(),
      );
      const combined = [...new Set([...formatedTitle, ...formatedSalutation])]
        .filter((t) => t !== 'herr' && t !== 'frau')
        .map((t) => t.charAt(0).toUpperCase() + t.slice(1));
      return combined.join(' ');
    },
    onS4cClick() {
      // Deferred so a dblclick can cancel it — see Jobs.vue for the same guard.
      clearTimeout(this.s4cClickTimer);
      this.s4cClickTimer = setTimeout(() => {
        this.copy(this.item.job.s4c_order_id);
      }, 250);
    },
    onS4cDblClick() {
      clearTimeout(this.s4cClickTimer);
      openS4cSalesOrder(this.item);
    },
    async copy(text) {
      if (text === null || text === undefined || text === '') return;
      const value = String(text);
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(value);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = value;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          try {
            document.execCommand('copy');
          } finally {
            textArea.remove();
          }
        }
        this.$bvToast.toast(`${value} copied`, {
          title: 'Success',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
        });
      } catch (err) {
        console.error('Failed to copy text:', err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.gid-job-summary {
  background: #ffffff;
  border: 1px solid #e1dfdd;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  margin-bottom: 1rem;
  overflow: hidden;
}

.gid-job-summary__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 0.9rem;
  background: #faf9f8;
  border-bottom: 1px solid #e1dfdd;
}

.gid-job-summary__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  min-width: 0;
}

.gid-job-summary__jobno {
  font-weight: 700;
  font-size: 1.05rem;
  color: #1a1100;
  white-space: nowrap;
}

.gid-job-summary__actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.gid-job-summary__refresh,
.gid-job-summary__close {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  .material-icons {
    font-size: 1rem;
    line-height: 1;
  }
}

.gid-job-summary__spin {
  animation: gid-job-summary-spin 0.8s linear infinite;
}

@keyframes gid-job-summary-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.gid-job-summary__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem 1.25rem;
  padding: 0.85rem 0.9rem;
}

.gid-job-summary__field {
  min-width: 0;
}

.gid-job-summary__label {
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8a8886;
  margin-bottom: 0.15rem;
}

.gid-job-summary__value {
  font-size: 0.9rem;
  color: #201a12;
  word-break: break-word;
}

.gid-job-summary__line {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.gid-job-summary__sub {
  font-size: 0.8rem;
  color: #605e5c;
  margin-top: 0.1rem;
}

.gid-job-summary__muted {
  color: #8a8886;
}

.gid-job-summary__copy {
  color: #9a6500;
  opacity: 0.65;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }
}

// Dark theme (cool slate, matches the job console tab strip)
.dark-mode .gid-job-summary {
  background: #202634;
  border-color: #333c4a;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
}
.dark-mode .gid-job-summary__bar {
  background: #1a1f2a;
  border-bottom-color: #333c4a;
}
.dark-mode .gid-job-summary__jobno {
  color: #eef1f6;
}
.dark-mode .gid-job-summary__value {
  color: #eef1f6;
}
.dark-mode .gid-job-summary__label {
  color: #9aa5b4;
}
.dark-mode .gid-job-summary__sub {
  color: #9aa5b4;
}
.dark-mode .gid-job-summary__muted {
  color: #7a8494;
}
.dark-mode .gid-job-summary__copy {
  color: #efc060;
}
</style>
